import { PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type { PipeInterface } from '@render/composables/usePipe'

interface SegmentConfig {
  writeKey: string
}

declare global {
  interface Window {
    analytics?: {
      initialized?: boolean
      invoked?: boolean
      methods?: string[]
      factory?: (e: string) => (...args: unknown[]) => unknown
      load?: (key: string, options?: unknown) => void
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _writeKey?: string
      // eslint-disable-next-line @typescript-eslint/naming-convention
      _loadOptions?: unknown
      // eslint-disable-next-line @typescript-eslint/naming-convention
      SNIPPET_VERSION?: string
      push: (...args: unknown[]) => void
      track: (event: string, properties?: Record<string, unknown>) => void
      page: (name?: string, properties?: Record<string, unknown>) => void
      identify: (userId: string, traits?: Record<string, unknown>) => void
      reset: () => void
      setAnonymousId: (id?: string) => void
      addSourceMiddleware: (
        fn: (opts: { payload: SegmentPayload; next: (p: SegmentPayload) => void }) => void,
      ) => void
    }
  }
}

interface SegmentPayload {
  obj: {
    properties: Record<string, unknown>
  }
}

function getCookieValue(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))

  return match ? decodeURIComponent(match[1] ?? '') : ''
}

const utmRequiredFields = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'fb_ads_id',
  'fb_adset_id',
]
function getUTMParams(pipeRef: PipeInterface): Record<string, unknown> {
  const trackingData = pipeRef.getData('var.global.trackingProperties') as
    | { queryParams?: Record<string, unknown> }
    | undefined
  const queryParams = trackingData?.queryParams ?? {}

  const result: Record<string, unknown> = {}
  for (const key of utmRequiredFields) {
    if (key in queryParams) {
      const parts = key.split('_')
      const camelKey = key.startsWith('utm_')
        ? parts
            .slice(1)
            .map((p, i) => (i === 0 ? p : p[0]!.toUpperCase() + p.slice(1)))
            .join('') + 'Utm'
        : key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      result[camelKey] = queryParams[key]
    }
  }

  return result
}

const segmentQueueMethods = [
  'trackSubmit',
  'trackClick',
  'trackLink',
  'trackForm',
  'pageview',
  'identify',
  'reset',
  'group',
  'track',
  'user',
  'ready',
  'alias',
  'debug',
  'page',
  'screen',
  'once',
  'off',
  'on',
  'addSourceMiddleware',
  'addIntegrationMiddleware',
  'setAnonymousId',
  'addDestinationMiddleware',
  'register',
]
const segmentContextMethods = ['track', 'screen', 'alias', 'group', 'page', 'identify']

function segmentFactory(method: string) {
  return (...args: unknown[]): unknown => {
    if (window.analytics?.initialized) {
      return (window.analytics as unknown as Record<string, (...a: unknown[]) => unknown>)[
        method
      ]?.(...args)
    }
    const call = [...args]
    if (segmentContextMethods.includes(method)) {
      const canonical = document.querySelector("link[rel='canonical']")
      call.push({
        /* eslint-disable @typescript-eslint/naming-convention */
        __t: 'bpc',
        c: canonical?.getAttribute('href') ?? undefined,
        p: location.pathname,
        u: location.href,
        s: location.search,
        t: document.title,
        r: document.referrer,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
    }
    call.unshift(method)
    window.analytics!.push(call)
    return window.analytics
  }
}

// Mirrors the queue-buffering half of Segment's official analytics.js snippet
// (see getHead() below) so window.analytics exists the instant the first
// event fires — independent of when that head script actually executes.
// Segment is registered with headClientOnly:true, so injection runs through a
// client-only Nuxt plugin's useHead() call, which can land after component
// mount on a direct page load; without this, trackEvent()/identifyUser()
// would silently drop events fired in that window.
//
// Deliberately does NOT set analytics.invoked or call .load(): the canonical
// snippet treats invoked===true as "already included, don't load the SDK
// again" — if we set that flag here, the canonical script would see it
// already set and skip inserting the real analytics.min.js <script> tag
// entirely, so the real SDK would never load. Leaving invoked unset means the
// canonical snippet's own (idempotent) method setup + .load() call still runs
// normally whenever it lands, safely re-declaring the same methods on the
// same array without losing anything already queued.
function ensureSegmentStub(): void {
  if (typeof window === 'undefined') return
  window.analytics = window.analytics || ([] as unknown as NonNullable<Window['analytics']>)
  if (typeof window.analytics.track === 'function') return
  for (const method of segmentQueueMethods) {
    ;(window.analytics as unknown as Record<string, unknown>)[method] = segmentFactory(method)
  }
}

function isSegmentAvailable(): boolean {
  if (typeof window === 'undefined') return false
  ensureSegmentStub()
  return (
    typeof window.analytics === 'object' &&
    window.analytics !== null &&
    typeof window.analytics.track === 'function'
  )
}

function getLocalStorageUserId(): string | null {
  try {
    const raw = localStorage.getItem('ajs_user_id')

    if (!raw) return null

    try {
      return JSON.parse(raw)
    } catch {
      return raw
    }
  } catch {
    return null
  }
}

const segment: PipeIntegration = (): PipeIntegrationDef => {
  let pipeRef: PipeInterface | null = null
  let middlewareRegistered = false

  function isSegmentEventAllowed(): boolean {
    const email = pipeRef?.getData('var.global.email') as string | undefined
    const lastPaymentId = pipeRef?.getData('var.global.lastPaymentId') as string | undefined

    return !!email && !!lastPaymentId
  }

  function getDefaultParams(): Record<string, unknown> {
    if (!pipeRef) return {}
    const constants = pipeRef.constants.value
    const distributionPath = (constants['system.distributionPath'] as string) ?? ''

    const afterPrefix = window.location.pathname.split(distributionPath)[1] ?? ''
    const pageSegment = afterPrefix.split('/').filter(Boolean)[0] ?? null

    return {
      /* eslint-disable @typescript-eslint/naming-convention */
      pageCode: pageSegment,
      page: pageSegment,
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer || null,
      title: document.title || null,
      pipe: distributionPath,
      pipe_id: pipeRef.data.value?.id ?? null,
      language: constants['system.language'] ?? null,
      geography: constants['system.geography'] ?? null,
      brand_code: constants['system.brandCode'] ?? null,
      brand: constants['system.brandCode'] ?? null,
      claritySessionID: getCookieValue('_clsk').split('^')[0] || null,
      eventSourceSystem: 'pipehouse',
      eventSourcePlatform: 'web',
      pipeSeries: 'pipehouse',
      pipe_series: 'pipehouse',
      klaviyoAccount: (constants['system.klaviyoPublicKey'] as string) || null,
      // Ingress AB-test trace id (see seedOriginDefaults in usePipe.ts):
      // current = most recent ingress landing's id. `origin` is intentionally
      // omitted from Segment instrumentation — only `current` is sent here
      // (origin still rides Klaviyo + the payments-API trackingProperties).
      currentIngressTraceId: pipeRef.getData('var.system.currentIngressTraceId') ?? null,
      /* eslint-enable @typescript-eslint/naming-convention */
    }
  }

  function mergeData(customData?: Record<string, unknown>): Record<string, unknown> {
    return {
      ...getDefaultParams(),
      ...(customData ?? {}),
      ...(pipeRef ? getUTMParams(pipeRef) : {}),
    }
  }

  function ensureMiddleware(): void {
    if (middlewareRegistered) return
    if (!window.analytics?.addSourceMiddleware) return
    middlewareRegistered = true

    window.analytics.addSourceMiddleware(({ payload, next }) => {
      const defaults = getDefaultParams()

      payload.obj.properties = { ...payload.obj.properties, ...defaults }

      const email = pipeRef?.getData('var.global.email') as string | undefined
      if (email) payload.obj.properties.email = email

      next(payload)
    })
  }

  function isUserIdentified(): boolean {
    // Legacy code - meaning, previously we had in CPPV3->API diet various bbrands - cf, hg etc.
    // So the segmentUserId came from Api-diet -> CPPv3 -> funnel. We stroed cf-12345 (brandslug + user_id) in LS.
    // Currently BE will send same as NNP doesn't have multiple DBs. Just same customerId
    const email = pipeRef?.getData('var.global.email') as string | undefined

    if (!email) return false

    return (
      getLocalStorageUserId() ===
      ((pipeRef?.getData('var.global.segmentUserId') as string | undefined) ?? '')
    )
  }

  function identifyUser(): void {
    if (!isSegmentAvailable()) return
    const segmentUserId = (pipeRef?.getData('var.global.segmentUserId') as string | undefined) || ''
    const email = pipeRef?.getData('var.global.email') as string | undefined
    if (isUserIdentified()) return

    if (!email) return

    try {
      localStorage.setItem('ajs_user_id', segmentUserId)
    } catch {
      // ignore storage errors
    }

    window.analytics!.identify(segmentUserId, { email })
  }

  async function trackEvent(
    eventName: string,
    customData?: Record<string, unknown>,
  ): Promise<void> {
    if (!isSegmentAvailable()) return
    if (!isSegmentEventAllowed()) return

    identifyUser()
    window.analytics!.track(eventName, mergeData(customData))
  }

  async function trackPageView(customData?: Record<string, unknown>): Promise<void> {
    if (!isSegmentAvailable()) return
    if (!isSegmentEventAllowed()) return

    identifyUser()
    const { pageCode } = getDefaultParams()
    console.log('go')
    window.analytics!.page((pageCode as string | null) ?? undefined, mergeData(customData))
  }

  return {
    getHead(config: SegmentConfig) {
      if (!config?.writeKey) return {}
      return {
        script: [
          {
            // Segment analytics.js queue stub — buffers calls before the async SDK loads.
            innerHTML: `(function(){var i="analytics",analytics=(window[i]=window[i]||[]);if(!analytics.initialize){if(analytics.invoked){window.console&&console.error&&console.error("Segment snippet included twice.")}else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","user","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"];analytics.factory=function(e){return function(){if(window[i].initialized)return window[i][e].apply(window[i],arguments);var n=Array.prototype.slice.call(arguments);if(["track","screen","alias","group","page","identify"].indexOf(e)>-1){var c=document.querySelector("link[rel='canonical']");n.push({__t:"bpc",c:(c&&c.getAttribute("href"))||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer})}n.unshift(e);analytics.push(n);return analytics}};for(var n=0;n<analytics.methods.length;n++){var key=analytics.methods[n];analytics[key]=analytics.factory(key)}analytics.load=function(key,n){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.setAttribute("data-global-segment-analytics-key",i);t.src="https://cdn.segment.com/analytics.js/v1/"+key+"/analytics.min.js";var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r);analytics._loadOptions=n};analytics.SNIPPET_VERSION="5.2.0";analytics.load(${JSON.stringify(config.writeKey)})}}})();`,
            tagPriority: 999,
          },
        ],
      }
    },

    setup(pipe: PipeInterface) {
      if (typeof window === 'undefined') return
      pipeRef = pipe

      // ensureSegmentStub() guarantees addSourceMiddleware exists
      // synchronously (queued until the real SDK loads), so the previous
      // poll-for-readiness workaround for the same head-script race is no
      // longer needed.
      ensureSegmentStub()
      ensureMiddleware()

      pipe.subscribe(PAYMENT_EVENTS.resetUser, () => {
        window.analytics?.reset?.()
        window.analytics?.setAnonymousId()
      })

      pipe.subscribe('navigation:show', async () => {
        await pipe.whenReady()
        await trackPageView()
      })
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutRequested,
        async (payload: Record<string, unknown>) => {
          await trackEvent('system event', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutLoaded,
        async (payload: Record<string, unknown>) => {
          await trackEvent('system event', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutLoadedConfirmation,
        async (payload: Record<string, unknown>) => {
          await trackEvent('system event', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCheckoutBeforePaymentCreated,
        async (payload: Record<string, unknown>) => {
          await trackEvent('click', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingPlanSelectedSlimPayload,
        async (payload: Record<string, unknown>) => {
          payload.clickName = 'Plan Selected'
          await trackEvent('click', payload)
        },
      )
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCustomEvent,
        async (payload: { eventName: string; customData?: Record<string, unknown> }) => {
          if (!payload.eventName) return

          const customData = payload.customData ?? {}
          customData.clickName = payload.eventName
          await trackEvent('click', customData)
        },
      )
    },
  }
}

export default segment
